import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MapPin, ArrowLeft, Activity, AlertTriangle, CheckCircle,
  Play, Pause, RefreshCw, Zap, TrendingUp, WifiOff, Eye
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.heat';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom marker icons based on health score
const createCustomIcon = (healthScore, hasCritical, isPulsing) => {
  let color = '#10b981'; // green
  if (hasCritical || healthScore < 50) {
    color = '#ef4444'; // red
  } else if (healthScore < 70) {
    color = '#f59e0b'; // yellow
  }

  const pulseClass = isPulsing ? 'pulse-marker' : '';

  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div class="relative ${pulseClass}">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="${color}" stroke="white" stroke-width="1.5">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
        </svg>
      </div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32]
  });
};

// Heatmap Layer Component
const HeatmapLayer = ({ customers }) => {
  const map = useMap();
  const heatLayerRef = useRef(null);

  useEffect(() => {
    if (!map || !customers || customers.length === 0) return;

    // Remove existing heat layer
    if (heatLayerRef.current) {
      map.removeLayer(heatLayerRef.current);
    }

    // Prepare heatmap data: [lat, lng, intensity]
    const heatData = customers
      .filter(c => c.location && c.location.latitude && c.location.longitude)
      .map(c => {
        // Intensity based on inverse health score (worse performance = higher intensity)
        const intensity = (100 - c.health_score) / 100;
        return [c.location.latitude, c.location.longitude, intensity];
      });

    if (heatData.length > 0) {
      // Create heat layer
      heatLayerRef.current = L.heatLayer(heatData, {
        radius: 30,
        blur: 25,
        maxZoom: 13,
        max: 1.0,
        gradient: {
          0.0: '#00ff00',
          0.3: '#ffff00',
          0.6: '#ff8800',
          1.0: '#ff0000'
        }
      }).addTo(map);
    }

    return () => {
      if (heatLayerRef.current) {
        map.removeLayer(heatLayerRef.current);
      }
    };
  }, [map, customers]);

  return null;
};

const NetworkPerformanceMap = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [summary, setSummary] = useState({ total: 0, critical: 0, warning: 0, good: 0 });
  const [liveMode, setLiveMode] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [nextRefresh, setNextRefresh] = useState(60);
  const [showHeatmap, setShowHeatmap] = useState(true);

  // India center coordinates
  const indiaCenter = [20.5937, 78.9629];

  useEffect(() => {
    fetchPerformanceData();
  }, []);

  useEffect(() => {
    if (!liveMode) return;

    // Auto-refresh every 60 seconds
    const refreshInterval = setInterval(() => {
      if (liveMode) {
        fetchPerformanceData(true);
        setLastUpdate(new Date());
        setNextRefresh(60);
      }
    }, 60 * 1000);

    // Countdown updates every second
    const countdownInterval = setInterval(() => {
      if (liveMode) {
        setNextRefresh(prev => prev > 0 ? prev - 1 : 60);
      }
    }, 1000);

    return () => {
      clearInterval(refreshInterval);
      clearInterval(countdownInterval);
    };
  }, [liveMode]);

  const fetchPerformanceData = async (silent = false) => {
    try {
      if (!silent) setLoading(true);

      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(`${API_URL}/api/monitoring/performance-map`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('📍 Performance Map Data Received:', {
          totalCustomers: data.customers?.length || 0,
          customersWithLocation: data.customers?.filter(c => c.location?.latitude).length || 0,
          summary: data.summary
        });
        
        // Log first customer for debugging
        if (data.customers && data.customers.length > 0) {
          console.log('📍 Sample Customer:', {
            name: data.customers[0].customer_name,
            ip: data.customers[0].static_ip,
            location: data.customers[0].location,
            health_score: data.customers[0].health_score
          });
        }
        
        setCustomers(data.customers || []);
        setSummary(data.summary || { total: 0, critical: 0, warning: 0, good: 0 });
        setLastUpdate(new Date());
        
        if (silent) {
          toast.success('Data refreshed', { duration: 1500 });
        } else {
          console.log('✅ Map data loaded successfully');
        }
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to load performance data:', response.status, errorText);
        toast.error('Failed to load performance data');
      }
    } catch (error) {
      console.error('❌ Error fetching performance data:', error);
      if (!silent) {
        toast.error('Failed to load performance data');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const runDiagnostics = async (customerId) => {
    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(`${API_URL}/api/monitoring/diagnostics/run`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        toast.success('Diagnostics started');
        setTimeout(() => fetchPerformanceData(true), 3000);
      } else {
        toast.error('Failed to run diagnostics');
      }
    } catch (error) {
      toast.error('Error running diagnostics');
    }
  };

  const viewCustomerDetails = (customerId) => {
    navigate(`/monitoring-dashboard/network-diagnostics`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Activity className="h-12 w-12 text-blue-600 animate-pulse mx-auto mb-4" />
          <p className="text-gray-600">Loading performance map...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
      <style>{`
        .pulse-marker {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.7;
            transform: scale(1.1);
          }
        }
        .custom-marker {
          background: none;
          border: none;
        }
      `}</style>

      {/* Header */}
      <div className="mb-4 bg-gradient-to-r from-blue-600 to-cyan-600 text-white p-4 rounded-lg shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/monitoring-dashboard')}
              className="text-white hover:bg-white/20"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back
            </Button>
            {/* Netix Logo */}
            <div className="bg-white rounded-lg p-2 shadow-md">
              <img 
                src="/netix-logo-horizontal.png" 
                alt="Netix Fibernet" 
                className="h-8 w-auto"
              />
            </div>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <MapPin className="h-6 w-6" />
                Real-Time Network Performance Map
              </h1>
              <p className="text-cyan-100 text-sm mt-1">
                Live network quality visualization
                {liveMode && (
                  <span className="ml-3">
                    • Last: {lastUpdate.toLocaleTimeString()} • Next: {nextRefresh}s
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => setShowHeatmap(!showHeatmap)}
              className="bg-white/20 hover:bg-white/30 text-white"
            >
              {showHeatmap ? 'Hide' : 'Show'} Heatmap
            </Button>
            <Button
              onClick={() => setLiveMode(!liveMode)}
              className={`${liveMode ? 'bg-green-500 hover:bg-green-600' : 'bg-gray-500 hover:bg-gray-600'} text-white`}
            >
              {liveMode ? (
                <>
                  <Pause className="h-4 w-4 mr-2" />
                  LIVE
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  Paused
                </>
              )}
            </Button>
            <Button
              onClick={() => fetchPerformanceData(true)}
              className="bg-white text-blue-600 hover:bg-blue-50"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
        <Card className="bg-gradient-to-br from-purple-500 to-indigo-600 text-white">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm opacity-90">Total Customers</div>
                <div className="text-3xl font-bold">{summary.total}</div>
              </div>
              <Activity className="h-10 w-10 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm opacity-90">Good Performance</div>
                <div className="text-3xl font-bold">{summary.good}</div>
              </div>
              <CheckCircle className="h-10 w-10 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-yellow-500 to-orange-500 text-white">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm opacity-90">Warning</div>
                <div className="text-3xl font-bold">{summary.warning}</div>
              </div>
              <AlertTriangle className="h-10 w-10 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-500 to-pink-600 text-white">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm opacity-90">Critical Issues</div>
                <div className="text-3xl font-bold">{summary.critical}</div>
              </div>
              <WifiOff className="h-10 w-10 opacity-80" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Map and Legend */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Map */}
        <div className="lg:col-span-3">
          <Card className="overflow-hidden">
            <div style={{ height: '500px', position: 'relative' }}>
              <MapContainer
                center={indiaCenter}
                zoom={5}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={true}
                key={`map-${customers.length}`}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                
                {/* Heatmap Layer */}
                {showHeatmap && customers.length > 0 && <HeatmapLayer customers={customers} />}

                {/* Customer Markers */}
                {customers.length > 0 && customers.map((customer) => {
                  if (!customer.location || !customer.location.latitude || !customer.location.longitude) {
                    console.log(`⚠️ Skipping customer ${customer.customer_name} - no location data`);
                    return null;
                  }

                  return (
                    <Marker
                      key={`marker-${customer.id}`}
                      position={[parseFloat(customer.location.latitude), parseFloat(customer.location.longitude)]}
                      icon={createCustomIcon(customer.health_score || 0, customer.has_critical_alert, customer.has_critical_alert)}
                    >
                      <Popup>
                        <div className="p-2 min-w-[250px]">
                          <h3 className="font-bold text-lg mb-2">{customer.customer_name}</h3>
                          <div className="text-sm space-y-1 mb-3">
                            <div><strong>IP:</strong> {customer.static_ip}</div>
                            <div><strong>Status:</strong> <span className={`font-semibold ${customer.status === 'active' ? 'text-green-600' : 'text-red-600'}`}>{customer.status.toUpperCase()}</span></div>
                            <div><strong>Health Score:</strong> <span className="font-semibold">{customer.health_score}/100</span></div>
                          </div>
                          
                          <div className="bg-gray-50 p-2 rounded mb-3 space-y-1 text-sm">
                            <div><strong>Latency:</strong> {customer.latency}ms</div>
                            <div><strong>Jitter:</strong> {customer.jitter}ms</div>
                            <div><strong>Packet Loss:</strong> {customer.packet_loss}%</div>
                            <div><strong>Bandwidth:</strong> {customer.bandwidth_usage}%</div>
                          </div>

                          {customer.alert_count > 0 && (
                            <div className="bg-red-50 border border-red-200 p-2 rounded mb-3 text-sm">
                              <div className="font-semibold text-red-800">⚠️ {customer.alert_count} Active Alert(s)</div>
                            </div>
                          )}

                          <div className="flex gap-2">
                            <button
                              onClick={() => runDiagnostics(customer.id)}
                              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-xs py-2 px-3 rounded flex items-center justify-center gap-1"
                            >
                              <Zap className="h-3 w-3" />
                              Run Diagnostics
                            </button>
                            <button
                              onClick={() => viewCustomerDetails(customer.id)}
                              className="flex-1 bg-gray-600 hover:bg-gray-700 text-white text-xs py-2 px-3 rounded flex items-center justify-center gap-1"
                            >
                              <Eye className="h-3 w-3" />
                              View Details
                            </button>
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  );
                })}
              </MapContainer>
            </div>
          </Card>
        </div>

        {/* Legend */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Legend</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2 text-sm">Health Status</h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-green-500"></div>
                    <span className="text-sm">Good (≥70)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-yellow-500"></div>
                    <span className="text-sm">Warning (50-69)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-red-500"></div>
                    <span className="text-sm">Critical (&lt;50)</span>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-semibold mb-2 text-sm">Indicators</h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-red-500 animate-pulse"></div>
                    <span className="text-sm">Critical Alert</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-gradient-to-r from-green-400 via-yellow-400 to-red-400 rounded"></div>
                    <span className="text-sm">Heatmap</span>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-semibold mb-2 text-sm">Quick Stats</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Avg Health:</span>
                    <span className="font-semibold">
                      {customers.length > 0 
                        ? (customers.reduce((sum, c) => sum + c.health_score, 0) / customers.length).toFixed(1)
                        : 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Active Alerts:</span>
                    <span className="font-semibold text-red-600">
                      {customers.reduce((sum, c) => sum + c.alert_count, 0)}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default NetworkPerformanceMap;
